import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { ShopService } from '@app/services/shops/shop.service';
import {
  CUTOFF_TIME_VARIABLE_SELECTOR,
  SHIPPING_RATE_VARIABLE_SELECTOR,
  SHIPPING_ZIPCODE_VARIABLE_SELECTOR,
} from '../../utils';

@Component({
  selector: 'product-page-configurations-basic-tab',
  templateUrl: './product-page-configurations-basic-tab.component.html',
  styleUrls: ['./product-page-configurations-basic-tab.component.scss'],
  standalone: false,
})
export class ProductPageConfigurationsBasicTabComponent implements OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shop: Shop;
  @Input() defaultIcon: string;
  @Input() pluginType: ShopPlugin.ProductPagePromise.PluginType;

  @Output() iconPreviewChange = new EventEmitter<unknown>();

  PluginType = ShopPlugin.ProductPagePromise.PluginType;
  lastPattern: string;
  resetPatternSubscription: Subscription;

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(public shopService: ShopService) {}

  onClearIcon(): void {
    this.formGroup.get('design.iconUrl').setValue('');
    this.formGroup.get('design.iconUrl').markAsDirty();
  }

  onTemplateCheck(value: boolean): void {
    this.formGroup
      .get('design.template')
      .setValue(
        value
          ? ShopPlugin.ProductPagePromise.DesignTemplate.DEFAULT
          : ShopPlugin.ProductPagePromise.DesignTemplate.MINIMAL,
      );

    switch (this.pluginType) {
      case ShopPlugin.ProductPagePromise.PluginType.PRO_SERVICE:
        this.handleProSerivceTemplatecheck(value);
        break;
      default:
      case ShopPlugin.ProductPagePromise.PluginType.DEFAULT:
        this.handleProductPromiseTemplateCheck(value);
        break;
    }
  }

  onShowPriceChanged(value: boolean): void {
    const patternControl = this.formGroup.get('fastestShipping.pattern');
    const pattern = patternControl.value;
    let newPattern;
    if (value) {
      let index = pattern.indexOf(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`);
      index = index === -1 ? pattern.indexOf(` order within ${CUTOFF_TIME_VARIABLE_SELECTOR}`) : index;
      newPattern =
        index !== -1
          ? `${pattern.slice(0, index)} for ${SHIPPING_RATE_VARIABLE_SELECTOR}${pattern.slice(index)}`
          : `${pattern} for ${SHIPPING_RATE_VARIABLE_SELECTOR}`;
    } else {
      newPattern = pattern
        .replaceAll(`for ${SHIPPING_RATE_VARIABLE_SELECTOR}`, '')
        .replaceAll(SHIPPING_RATE_VARIABLE_SELECTOR, '')
        .trim();
    }
    patternControl.setValue(newPattern);
  }

  onShowCutoffChanged(value: boolean, configuration: 'fastestShipping' | 'freeShipping'): void {
    const patternControl = this.formGroup.get(`${configuration}.pattern`);
    const pattern = patternControl.value;
    const newPattern = value
      ? `${pattern} order within ${CUTOFF_TIME_VARIABLE_SELECTOR}`
      : pattern
          .replaceAll(`order within ${CUTOFF_TIME_VARIABLE_SELECTOR}`, '')
          .replaceAll(CUTOFF_TIME_VARIABLE_SELECTOR, '')
          .trim();
    patternControl.setValue(newPattern);
  }

  private handleProductPromiseTemplateCheck(value: boolean): void {
    const patternControl = this.formGroup.get('fastestShipping.pattern');
    const pattern = patternControl.value;
    let newPattern;
    if (value) {
      this.lastPattern = pattern;
      newPattern = pattern
        .replaceAll(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`, '')
        .replaceAll(SHIPPING_ZIPCODE_VARIABLE_SELECTOR, '')
        .trim();
    } else {
      const index = pattern.indexOf(` order within ${CUTOFF_TIME_VARIABLE_SELECTOR}`);
      newPattern =
        this.lastPattern ??
        (index !== -1
          ? `${pattern.slice(0, index)} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}${pattern.slice(index)}`
          : `${pattern} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`);
    }
    this.resetPatternSubscription?.unsubscribe();
    patternControl.setValue(newPattern);
    this.resetPatternSubscription = patternControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.lastPattern = null;
    });
  }

  private handleProSerivceTemplatecheck(useExtendedDesign: boolean): void {
    const inStockPattern = this.formGroup.get('inventoryConfig.inStockPattern');
    const outOfStockPattern = this.formGroup.get('inventoryConfig.outOfStockPattern');
    const partiallyInStockPattern = this.formGroup.get('inventoryConfig.partiallyInStockPattern');
    const fallbackText = this.formGroup.get('inventoryConfig.fallbackText');
    if (useExtendedDesign) {
      this.formGroup.get('inventoryConfig').patchValue({
        inStockPattern: inStockPattern.value.replaceAll(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`, ''),
        outOfStockPattern: outOfStockPattern.value.replaceAll(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`, ''),
        partiallyInStockPattern: partiallyInStockPattern.value.replaceAll(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`, ''),
        fallbackText: fallbackText.value.replaceAll(` ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`, ''),
      });
    } else {
      this.formGroup.get('inventoryConfig').patchValue({
        inStockPattern: `${inStockPattern.value} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`,
        outOfStockPattern: `${outOfStockPattern.value} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`,
        partiallyInStockPattern: `${partiallyInStockPattern.value} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`,
        fallbackText: `${fallbackText.value} ${SHIPPING_ZIPCODE_VARIABLE_SELECTOR}`,
      });
    }
  }
}
